// Клиент `/api/v1/auth/*` и `/api/v1/me`.
//
// Типы вшиты руками — кодогенерации из OpenAPI в проекте нет.
// Сигнатуры обязаны совпадать с pydantic-схемами backend'а
// (`backend/core/auth/schemas.py`); расхождение ловится только
// прогоном против живого стека, автоматической сверки нет.

import { authedFetch, HTTP_UNAUTHORIZED } from "../../auth/authedFetch";

// HTTP-коды, которые мы интерпретируем как бизнес-ошибки (а не
// «бэк прилёг»). Выносим в константы — тестам и хукам читать
// `HTTP_CONFLICT`, а не голую 409.
export const HTTP_CONFLICT = 409;
// 403 от `switchOrganization` — членство в организации отозвали между
// `fetchAvailableOrganizations` и `switch` (SelectOrgPage показывает
// «вы больше не состоите в этой организации», а не разлогинивает —
// разлогин только на 401, см. `authedFetch`).
export const HTTP_FORBIDDEN = 403;
// Re-export для удобства потребителей (LoginPage показывает
// «Неверный email или пароль» именно на 401).
export { HTTP_UNAUTHORIZED };

export interface AuthCredentialsRequest {
  readonly email: string;
  readonly password: string;
}

export interface AuthTokenResponse {
  /**
   * JWT. После #916 фронт его НЕ хранит и НЕ использует: токен живёт
   * в HttpOnly cookie `baton_session`, которую ставит backend
   * (Set-Cookie на login/register). Поле остаётся в типе, т.к.
   * backend по-прежнему кладёт токен в тело ответа для программных /
   * тестовых клиентов (Bearer-fallback). Фронт читает только id'шники.
   */
  readonly token: string;
  readonly user_id: string;
  /**
   * Хинт, не грант (ADR-0038 §2): org-less сессия на фазе 1 — реальный
   * ответ `login`/`register`, активная организация ещё не выбрана.
   * Фронт НИКОГДА не пишет это поле в стор как активную org —
   * единственные источники активной org: успешный `switchOrganization`
   * и `GET /me.active_org_id`.
   */
  readonly org_id: string | null;
}

export interface MeResponse {
  readonly user_id: string;
  readonly user_email: string;
  readonly remember_sessions: boolean;
  /** Активная организация сессии; `null` — org-less (ADR-0038). */
  readonly active_org_id: string | null;
}

export interface AvailableOrgResponse {
  readonly org_id: string;
  readonly slug: string;
  readonly name: string;
}

/**
 * HTTP-ошибка с числовым `status`. Хуки (useLogin/useRegister)
 * по нему различают 401 (неверный пароль) и 409 (email занят) —
 * без парсинга body.
 */
export class HttpError extends Error {
  public readonly status: number;
  public constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

function buildJsonHeaders(): HeadersInit {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

export async function registerUser(
  body: AuthCredentialsRequest,
): Promise<AuthTokenResponse> {
  // skipAutoLogout: 401 здесь невозможен (register не требует
  // токена), но и 409 не должен дергать logout — мы ещё не
  // залогинены. Документируем намеренный отказ от auto-logout
  // в auth endpoint'ах.
  const response = await authedFetch("/api/v1/auth/register", {
    method: "POST",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
    skipAutoLogout: true,
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `register: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as AuthTokenResponse;
}

export async function loginUser(
  body: AuthCredentialsRequest,
): Promise<AuthTokenResponse> {
  const response = await authedFetch("/api/v1/auth/login", {
    method: "POST",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
    skipAutoLogout: true,
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `login: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as AuthTokenResponse;
}

export async function fetchMe(): Promise<MeResponse> {
  const response = await authedFetch("/api/v1/me", {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(response.status, `me: HTTP ${String(response.status)}`);
  }
  return (await response.json()) as MeResponse;
}

export async function logoutUser(): Promise<void> {
  // Зовём backend, чтобы он отозвал серверную сессию И прислал
  // Set-Cookie с Max-Age=0 (удаление HttpOnly cookie `baton_session`)
  // — JS сам cookie стереть не может. Best-effort: даже если backend
  // недоступен, frontend всё равно очистит локальный identity-флаг.
  try {
    await authedFetch("/api/v1/auth/logout", {
      method: "POST",
      skipAutoLogout: true,
    });
  } catch {
    // intentional: ошибки backend'а не блокируют локальную очистку
    // identity-флага (cleanup на стороне UI делает вызывающий хук).
  }
}

export async function fetchAvailableOrganizations(): Promise<
  AvailableOrgResponse[]
> {
  const response = await authedFetch("/api/v1/me/organizations", {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `organizations: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as AvailableOrgResponse[];
}

export async function switchOrganization(
  orgId: string,
): Promise<AuthTokenResponse> {
  // Без skipAutoLogout: 401 здесь значит, что серверная сессия и правда
  // протухла (не «пароль неверный», как на login) — авто-logout корректен.
  const response = await authedFetch("/api/v1/me/organizations/switch", {
    method: "POST",
    headers: buildJsonHeaders(),
    body: JSON.stringify({ org_id: orgId }),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `switch org: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as AuthTokenResponse;
}
